import { formatDecimal } from '../helpers/formatDecimal';
import { formatNumberByThousands } from '../helpers/formatNumberByThousands';
import { getAccountTypeTranslationKey } from '../helpers/getAccountTypeTranslationKey';
import { Consolidation } from '../types/ConsolidationTypes';
import useSettings from './useSettings';
import useTranslation from './useTranslation';

export const useConsolidationTexts = () => {
    const translate = useTranslation();
    const { countryCode } = useSettings();

    const getTitle = (consolidation: Consolidation) => {
        let title = '';

        if (consolidation.accountType) {
            title = translate(getAccountTypeTranslationKey(consolidation.accountType));
        }
        if (consolidation.creditorName) {
            if (title) {
                title += ', ';
            }
            title += consolidation.creditorName;
        }
        if (consolidation.currentBalance?.amount) {
            if (title) {
                title += ', ';
            }
            if (consolidation.consolidationType === 'PARTIAL') {
                title += `${formatNumberByThousands(
                    formatDecimal(consolidation?.consolidationAmount?.amount || 0, countryCode),
                )} ${translate('field.currency.label')} ${translate('field.partial-amount.of')} ${formatNumberByThousands(
                    formatDecimal(consolidation.currentBalance?.amount || 0, countryCode),
                )} ${translate('field.currency.label')}`;
            } else {
                title += `${formatNumberByThousands(
                    formatDecimal(consolidation.currentBalance.amount, countryCode),
                )} ${translate('field.currency.label')}`;
            }
        }

        return title;
    };

    const getCaption = (consolidation: Consolidation) => {
        let caption = '';

        if (consolidation.nominalInterestRate) {
            caption = `${consolidation.nominalInterestRate}% ${translate('field.interest.label')}`;
        }

        if (consolidation.tenor && consolidation.tenor !== 0) {
            if (caption) {
                caption += ', ';
            }

            caption += `${consolidation.tenor} ${translate('field.months.label')}`;
        }

        if (consolidation.consolidationType === 'PARTIAL') {
            if (caption) {
                caption += ', ';
            }
            const remainingDebt = Math.max(
                (consolidation.currentBalance?.amount || 0) - (consolidation.consolidationAmount?.amount || 0),
                0,
            );
            caption += `${translate('field.remaining-debt.label')} ${formatNumberByThousands(
                formatDecimal(remainingDebt, countryCode),
            )} ${translate('field.currency.label')}`;
        }

        return caption;
    };

    return { getTitle, getCaption };
};
